import React, { ReactNode, useId, useRef } from 'react';
import { Flex } from 'glints-aries/es';
import {
  Icon,
  InlineError,
  OutlineButton,
  Typography,
} from 'glints-aries/es/@next';
import { Neutral } from 'glints-aries/es/@next/utilities/colors';
import { space8 } from 'glints-aries/es/@next/utilities/spacing';
import { compact } from 'lodash-es';
import { useController } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import useUploadFile from '../../../../../common/hooks/requests/useUploadFile';
import { useIsViewOnlyMode } from '../../FormContext';
import { useCompanyVerificationTracking } from '../../hooks/useCompanyVerificationTracking';
import { getAssetFileNameFromURL, isURL } from '../../utils';
import FileItem from '../FileItem';
import { MultiFileUploaderProps } from './types';

export default function MultiFileUploaderList({
  ...rest
}: MultiFileUploaderProps) {
  const cachedFilesRef = useRef<Record<string, File>>({});

  const setFileToCache = (key: string, file: File) => {
    cachedFilesRef.current[key] = file;
  };

  return (
    <Flex flexDirection="column" gap={space8}>
      <ListVariantUploadButton {...rest} setFileToCache={setFileToCache} />
      <ListVariantFileItems {...rest} cachedFiles={cachedFilesRef.current} />
    </Flex>
  );
}
const DEFAULT_MAX_FILE_SIZE = 10;

function ListVariantUploadButton({
  assetType,
  fieldName,
  maxFileSizeInMB = DEFAULT_MAX_FILE_SIZE,
  maxImages = 1,
  setFileToCache,
  disabled,
}: Pick<
  MultiFileUploaderProps,
  'fieldName' | 'assetType' | 'maxFileSizeInMB' | 'maxImages' | 'disabled'
> & {
  setFileToCache: (key: string, file: File) => void;
}) {
  const {
    field: { onChange, value: fieldValue },
  } = useController({
    name: fieldName,
  });
  const value = (fieldValue as string[]) || [];
  const inputElemId = useId();
  const { trackCompanyVerificationMultiFileUploadListEvent } =
    useCompanyVerificationTracking();

  const { uploadFile, loading, error } = useUploadFile({
    assetType,
    maxSizeInMB: maxFileSizeInMB,
    allowedMimeTypes: ['application/pdf', 'image/jpeg', 'image/png'],
    onSuccess: (uploadedFilePath, file) => {
      onChange(compact([...value, uploadedFilePath]));
      setFileToCache(uploadedFilePath, file);
      trackCompanyVerificationMultiFileUploadListEvent(fieldName);
    },
  });
  const isViewOnly = useIsViewOnlyMode();

  if (value.length >= maxImages || isViewOnly) {
    return null;
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
  };

  return (
    <Flex flexDirection="column" gap={space8}>
      <input
        type="file"
        id={inputElemId}
        accept="application/pdf, image/jpeg, image/png"
        hidden={true}
        onChange={handleFileChange}
      />
      <label htmlFor={inputElemId}>
        <OutlineButton
          icon={<Icon name="ri-upload-fill" />}
          style={{
            maxWidth: 'max-content',
          }}
          loading={loading}
          onClick={e => e.currentTarget.parentElement?.click()}
          disabled={disabled}
        >
          <Typography variant="button">
            <FormattedMessage
              id="interactive-select-file-max"
              defaultMessage="Select File (Max: {max})"
              values={{
                max: maxImages,
              }}
            />
          </Typography>
        </OutlineButton>
      </label>
      {error && <InlineError text={error} />}
      <Typography variant="subtitle2" color={Neutral.B40}>
        <FormattedMessage
          id="text-acceptable-formats-max"
          defaultMessage="<bold>Acceptable formats:</bold> pdf, jpg, jpeg, png | <bold>Maximum size: {maxSize}MB</bold>"
          values={{
            bold: (...chunks: ReactNode[]) => (
              <Typography variant="caption" as="span" color={Neutral.B40}>
                {chunks}
              </Typography>
            ),
            maxSize: maxFileSizeInMB,
          }}
        />
      </Typography>
    </Flex>
  );
}

function ListVariantFileItems({
  fieldName,
  cachedFiles,
}: Pick<MultiFileUploaderProps, 'fieldName'> & {
  cachedFiles: Record<string, File>;
}) {
  const {
    field: { onChange, value: fieldValue },
  } = useController({
    name: fieldName,
  });
  const value = (fieldValue as string[]) || [];
  const isViewOnly = useIsViewOnlyMode();

  if (value.length <= 0) {
    return null;
  }

  const handleRemoveFile = (itemToRemove: string) => {
    onChange(compact(value.filter(value => value !== itemToRemove)));
  };

  return (
    <Flex flexDirection="column" gap={space8}>
      {value.map(item => {
        const cachedFile = cachedFiles[item];
        const fileName = cachedFile?.name;
        const fileSize = (cachedFile?.size / 1024 / 1024).toFixed(2);
        const selectedFile = isURL(item)
          ? {
              assetURL: item,
              assetFileName: getAssetFileNameFromURL(item),
              disablePreview: false,
            }
          : {
              assetURL: cachedFile ? URL.createObjectURL(cachedFile) : item,
              assetFileName: cachedFile ? `${fileName} (${fileSize} MB)` : item,
              disablePreview: !cachedFile,
            };
        return (
          <FileItem
            key={selectedFile.assetURL}
            file={selectedFile}
            handleRemoveFile={() => {
              if (isViewOnly) return;
              handleRemoveFile(item);
            }}
          />
        );
      })}
    </Flex>
  );
}
